/* @flow */
import * as React from 'react';
import cx from 'classnames';
import decamelizeKeys from 'decamelize-keys';
import Icon from 'components/Icon.jsx';
import TooltipTrigger from 'components/TooltipTrigger.jsx';
import ScrollLoadZone from 'components/ScrollLoadZone.jsx';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator.jsx';

import {
  ComposableTable,
  composeMultipleCellsWithWrapper,
  getUserAvatarCellGenerator,
  getCheckboxCellGenerator,
  getFamilyCellGenerator,
  getIconCellGenerator,
  getTextCellGenerator,
  getArrayTextCellGenerator,
  getTimestampCellGenerator,
} from 'components/ComposableTable';

import Text from 'components/Text.jsx';
import Link from 'components/Link.jsx';
import Group from 'entities/Group';
import ModalTrigger from 'components/ModalTrigger.jsx';
import UsersInviteModal from 'users/components/UsersInviteModal';
import Row from 'components/Row.jsx';

import type { UserRole, GroupInfo } from './PeopleList.jsx';
import type { PeopleListRow } from './PeopleListTableDataContainer.jsx';
import type { ColumnHeader, SortDirection } from 'components/ComposableTable';

type Props = {
  entityUuid: string,
  hasNextPage: boolean,
  loading: boolean,
  fetchMore: Function,
  members: PeopleListRow[],
  role: UserRole,
  onPersonClicked: Object => void,
  groupInfo: GroupInfo,
  onUserAddedToSelection: PeopleListRow => void,
  onUserRemovedFromSelection: PeopleListRow => void,
  onSort: (sortColumn: string, sortDirection: ?SortDirection) => void,
  selectedUserIds: number[],
};

export default class PeopleListTable extends React.Component<Props> {
  toggleUser = (row: PeopleListRow) => {
    const { selectedUserIds, onUserAddedToSelection, onUserRemovedFromSelection } = this.props;
    if (selectedUserIds.includes(row.user.id)) {
      onUserRemovedFromSelection(row);
    } else {
      onUserAddedToSelection(row);
    }
  };

  handleAvatarClicked = (event: SyntheticEvent<>, row: PeopleListRow) => {
    event.stopPropagation();
    const { onUserAddedToSelection } = this.props;
    onUserAddedToSelection(row);
  };

  handleCheckboxClicked = (event: SyntheticEvent<>, row: PeopleListRow) => {
    event.stopPropagation();
    this.toggleUser(row);
  };

  columnHeaderForColumn = (columnName: string): ColumnHeader => {
    const columnMap = {
      classes: {
        name: 'Classes',
        icon: {
          iconName: 'question-circle',
          tooltip: 'The number of classes the user is in',
        },
      },
      familyMembers: { name: 'Family members' },
      grade: {
        name: 'Grade',
        sortValue: 'grade',
      },
      lastAnnouncementSent: {
        name: 'Last announcement sent',
        sortValue: 'last_broadcast_sent_on',
      },
      name: {
        name: 'Name',
        sortValue: 'first_name',
      },
      role: { name: 'Role' },
      source: {
        name: 'SIS-linked',
        icon: {
          iconName: 'question-circle',
          tooltip:
            'SIS-linked users are linked to your school’s database. Not SIS-linked users signed up individually or were invited by others.',
        },
      },
      title: { name: 'Title' },
    };

    return columnMap[columnName];
  };

  mapRoleToHeaders = {
    all: ['name', 'source', 'role', 'classes'],
    teacher: ['name', 'source', 'lastAnnouncementSent', 'classes'],
    student: ['name', 'source', 'grade', 'classes', 'familyMembers'],
    admin: ['name', 'title', 'lastAnnouncementSent'],
    parent: ['name', 'source', 'classes', 'familyMembers'],
    unknown: ['name', 'source', 'role', 'classes'],
  };

  columnHeadersForRole = (role: UserRole): Array<ColumnHeader> => {
    return this.mapRoleToHeaders[role].map(name => this.columnHeaderForColumn(name));
  };

  columnGeneratorsForRole = (role: UserRole): Array<Function> => {
    const { selectedUserIds } = this.props;
    const iconProps = {
      size: 'large',
    };

    const components = {
      name: composeMultipleCellsWithWrapper(
        [
          composeMultipleCellsWithWrapper(
            [
              getCheckboxCellGenerator({
                onClickAction: this.handleCheckboxClicked,
                checked: data => selectedUserIds.includes(data.user.id),
              }),
              getUserAvatarCellGenerator({
                userField: row => row.user,
                onClickAction: this.handleAvatarClicked,
                userAvatarProps: {
                  className: cx({
                    hidden: selectedUserIds.length > 0,
                  }),
                },
              }),
            ],
            'PeopleList-name-container-avatar-checkbox'
          ),
          getTextCellGenerator(row => row.user.name, {
            className: 'PeopleList-name-container-text',
          }),
        ],
        'PeopleList-name-container'
      ),
      classes: getTextCellGenerator(row => row.orgUserStats.groupsJoinedCount),
      familyMembers: getFamilyCellGenerator(
        row => row.user.familySummary,
        row => ({
          userId: row.user.id,
          userUuid: row.user.uuid,
          canAddChild: row.userInteractionPermissions.canAddChild,
          canAddParent: row.userInteractionPermissions.canAddParent,
        })
      ),
      grade: getTextCellGenerator('grade'),
      lastAnnouncementSent: getTimestampCellGenerator(
        row =>
          row.orgUserStats.lastBroadcastSentOn ? row.orgUserStats.lastBroadcastSentOn * 1000 : null
      ),
      source: getIconCellGenerator('rosteredStatus', {
        true: (
          <TooltipTrigger tooltip="SIS-linked" placement="top">
            <span>
              <Icon {...iconProps} name="link-linked" color="shamrock" />
            </span>
          </TooltipTrigger>
        ),
        false: (
          <TooltipTrigger tooltip="Not SIS-linked" placement="top">
            <span>
              <Icon {...iconProps} name="link-unlinked" color="koala" />
            </span>
          </TooltipTrigger>
        ),
      }),
      role: getArrayTextCellGenerator('roles', ', ', r => r.charAt(0).toUpperCase() + r.slice(1)),
      title: getTextCellGenerator('title'),
    };

    return this.mapRoleToHeaders[role].map(name => components[name]);
  };

  renderNoResultsView = () => {
    const { entityUuid, groupInfo } = this.props;
    const group = Group.fromJSON(decamelizeKeys(groupInfo));

    return (
      <div data-test="no-results-view">
        <Text align="center" size="medium">
          There’s no one here!{' '}
          <ModalTrigger modal={<UsersInviteModal group={group} />}>
            <Link>Add people</Link>
          </ModalTrigger>{' '}
          directly or{' '}
          <Link to={`/organizations/${entityUuid}/settings/organization/sis_sync`}>
            sync your members
          </Link>{' '}
          with Remind.
        </Text>
      </div>
    );
  };

  render() {
    const { fetchMore, hasNextPage, loading, role, members, onPersonClicked, onSort } = this.props;
    const columnHeaders = this.columnHeadersForRole(role);
    const columnGenerators = this.columnGeneratorsForRole(role);

    return (
      <div>
        <Row size="large">
          <ComposableTable
            className="PeopleList-body"
            columnHeaders={columnHeaders}
            columnGenerators={columnGenerators}
            rows={members}
            keyField={data => data.user.uuid}
            handleSort={onSort}
            handleRowClick={onPersonClicked}
            loading={loading}
          />
        </Row>
        {hasNextPage && (
          <ScrollLoadZone threshold={500} onZoneActivated={fetchMore}>
            <CenteredLoadingIndicator height={50} size="small" />
          </ScrollLoadZone>
        )}
        {members && members.length === 0 && this.renderNoResultsView()}
      </div>
    );
  }
}
